import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  LinearProgress,
  Avatar,
} from "@mui/material";
import { tokens } from "../../theme";
import { useLayoutEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import LoadingBackDrop from "../global/LoadingBackDrop";
import { getPayload } from "../../services/api";
import dayjs, { utc } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import ConcreteCubeAccordingData from "./ConcreteCubeAccordingData";
import {
  AssignmentTurnedIn,
  CalendarMonth,
  DeleteForever,
  FileDownload,
  FormatListBulleted,
  PictureAsPdf,
  ViewInAr,
  Science as ScienceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  LocalShipping as TransportIcon,
  Construction as ConstructionIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Colombo");

// Styled components
const CubeCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  transition: "all 0.3s ease",
  border: "1px solid #e0e0e0",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
}));

const CubesToBeCasted = ({
  castingCubesData,
  setSelectedRequestId,
  setOpenLabSheetDialog,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Toggle card expansion
  const toggleCardExpansion = (index) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      return dayjs(dateString).format("YYYY-MM-DD");
    } catch {
      return dateString;
    }
  };

  // Calculate days until test
  const getDaysUntilTest = (testDate) => {
    if (!testDate) return null;
    const today = dayjs();
    const test = dayjs(testDate);
    const days = test.diff(today, "day");
    return days;
  };

  // Get status color based on days until test
  const getUrgencyColor = (days) => {
    if (days === null) return "default";
    if (days < 0) return "error"; // Overdue
    if (days <= 3) return "warning"; // Due soon
    if (days <= 7) return "info"; // Upcoming
    return "success"; // Scheduled
  };

  // Get status label
  const getUrgencyLabel = (days) => {
    if (days === null) return "Not Scheduled";
    if (days < 0) return `${Math.abs(days)} Days Overdue`;
    if (days === 0) return "Due Today";
    if (days === 1) return "Due Tomorrow";
    return `Due in ${days} Days`;
  };

  // Get progress based on cube status
  const getCubeProgress = (cube) => {
    let steps = 0;
    if (cube.castingDate) steps++;
    if (cube.finalized) steps++;
    if (cube.checked) steps++;
    if (cube.checkedByEngineer) steps++;
    return (steps / 4) * 100;
  };

  // Group cubes by status
  const groupedCubes = useMemo(() => {
    return {
      pending: castingCubesData.filter(
        (cube) => !cube.finalized && !cube.checked,
      ),
      inProgress: castingCubesData.filter(
        (cube) => cube.finalized || cube.checked,
      ),
      completed: castingCubesData.filter((cube) => cube.checkedByEngineer),
    };
  }, [castingCubesData]);

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorFn: (row) =>
          `JOB/${row?.labRequest.year}/${String(
            row?.labRequest.autoIdByYear,
          ).padStart(4, "0")}`,
        header: "Job No",
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="primary">
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "labRequest.regDate",
        header: "Job Date",
        size: 80,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "labRequest.institute.instituteName",
        header: "Institute/Contractor",
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={500}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "castingDate",
        header: "Casting Date",
        size: 80,
        Cell: ({ cell }) => (
          <Chip
            label={formatDate(cell.getValue())}
            size="small"
            color="primary"
            variant="outlined"
            icon={<CalendarMonth />}
          />
        ),
      },
      {
        accessorKey: "testDate",
        header: "Test Date",
        size: 80,
        Cell: ({ cell }) => {
          const days = getDaysUntilTest(cell.getValue());
          return (
            <Box>
              <Chip
                label={formatDate(cell.getValue())}
                size="small"
                color={getUrgencyColor(days)}
                variant="outlined"
                icon={<ScheduleIcon />}
              />
              {days !== null && (
                <Typography
                  variant="caption"
                  display="block"
                  color={getUrgencyColor(days)}
                >
                  {getUrgencyLabel(days)}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "startingSerialNo",
        header: "Serial No",
        size: 80,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="warning.main">
            #{cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "quantity",
        header: "Qty",
        size: 60,
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: castingCubesData,
    positionActionsColumn: "last",
    initialState: {
      density: "compact",
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100, 150, 200],
      showFirstButton: true,
      showLastButton: true,
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.original?.labRequest.paid ? "white" : "#fff9c4",
        "&:hover": {
          backgroundColor: row.original?.labRequest.paid
            ? "#f5f5f5"
            : "#fff176",
        },
      },
    }),
  });

  // Summary stats
  const totalCubes = castingCubesData.length;
  const unpaidCubes = castingCubesData.filter(
    (cube) => !cube.labRequest.paid,
  ).length;
  const overdueCubes = castingCubesData.filter((cube) => {
    const days = getDaysUntilTest(cube.testDate);
    return days !== null && days < 0;
  }).length;

  return (
    <Box sx={{ width: "100%", mb: 4 }}>
      {loading && <LoadingBackDrop openBackDrop={loading} />}

      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#1a237e",
            mb: 1,
          }}
        >
          <ScienceIcon sx={{ fontSize: 30, color: "#1976d2" }} />
          Concrete Cubes To Be Casted
        </Typography>
      </Box>

      {/* Cards View */}
      <Grid container spacing={2}>
        {castingCubesData.map((data, index) => {
          const daysUntilTest = getDaysUntilTest(data.testDate);
          const urgencyColor = getUrgencyColor(daysUntilTest);
          const progress = getCubeProgress(data);
          const isExpanded = expandedCards[index] || false;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <CubeCard
                elevation={0}
                sx={{
                  borderLeft: `4px solid ${
                    !data?.labRequest.paid
                      ? "#f44336"
                      : urgencyColor === "error"
                        ? "#ff9800"
                        : urgencyColor === "warning"
                          ? "#ff9800"
                          : "#4caf50"
                  }`,
                  bgcolor: !data?.labRequest.paid ? "#fff5f5" : "white",
                  height: isExpanded ? "auto" : "auto",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  {/* Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={1}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        color={!data?.labRequest.paid ? "error" : "primary"}
                        sx={{ fontSize: "1rem" }}
                      >
                        {`JOB/${data?.labRequest.year}/${String(data?.labRequest.autoIdByYear).padStart(4, "0")}`}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        {formatDate(data?.labRequest.regDate)}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => {
                        setSelectedRequestId(data);
                        setOpenLabSheetDialog(true);
                      }}
                      color="primary"
                      size="small"
                      sx={{
                        bgcolor: "#e3f2fd",
                        "&:hover": { bgcolor: "#bbdefb" },
                      }}
                    >
                      <FileDownload sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>

                  {/* Institute & Project */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <BusinessIcon sx={{ fontSize: 16 }} />
                      {data?.labRequest.institute.instituteName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {data?.labRequest.project}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Key Info Grid */}
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarMonth
                          sx={{ fontSize: 16, color: "primary.main" }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            Casting
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDate(data?.castingDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ScheduleIcon
                          sx={{ fontSize: 16, color: urgencyColor }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            Test
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={urgencyColor}
                          >
                            {formatDate(data?.testDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <FormatListBulleted
                          sx={{ fontSize: 16, color: "warning.main" }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            Serial No
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="warning.main"
                          >
                            #{data?.startingSerialNo}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ViewInAr sx={{ fontSize: 16, color: "info.main" }} />
                        <Box>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            Quantity
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="info.main"
                          >
                            {data?.quantity}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Urgency & Status */}
                  <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                    {!data?.labRequest.paid && (
                      <StatusChip
                        icon={<CancelIcon />}
                        label="UNPAID"
                        color="error"
                        size="small"
                      />
                    )}
                    {daysUntilTest !== null && (
                      <StatusChip
                        icon={<ScheduleIcon />}
                        label={getUrgencyLabel(daysUntilTest)}
                        color={urgencyColor}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {/* Progress Bar */}
                  <Box sx={{ mb: 1 }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" fontWeight={500}>
                        Progress
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="primary"
                      >
                        {Math.round(progress)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: progress === 100 ? "#4caf50" : "#1976d2",
                        },
                      }}
                    />
                  </Box>

                  {/* Expand/Collapse Button */}
                  <Button
                    size="small"
                    fullWidth
                    onClick={() => toggleCardExpansion(index)}
                    endIcon={
                      isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                    sx={{ mt: 1 }}
                  >
                    {isExpanded ? "Show Less" : "Show Details"}
                  </Button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ my: 1 }} />
                      <ConcreteCubeAccordingData
                        start={data.startingSerialNo}
                        quantity={data.quantity}
                      />

                      {/* Additional Info */}
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Contact Person
                        </Typography>
                        <Typography variant="body2">
                          {data?.labRequest.contactPerson || "N/A"}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          Contact No
                        </Typography>
                        <Typography variant="body2">
                          {data?.labRequest.contactNo ||
                            data?.labRequest.mobileNo ||
                            "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </CubeCard>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CubesToBeCasted;
